<script setup lang="ts">
import { mdiFilterVariant, mdiMagnify, mdiEyeOffOutline } from '@quasar/extras/mdi-v7';
import type {
  ListItemStatus,
  ListSortKey,
} from 'shared/composables/useListFilters/useListFilters';
import { LIST_SORT_KEYS } from 'shared/composables/useListFilters/useListFilters';

interface Props {
  selectedStatuses: ListItemStatus[];
  activeFilterCount: number;
  shownCount?: number;
  totalCount?: number;
  disable?: boolean;
}

defineProps<Props>();

const hideCompleted = defineModel<boolean>('hideCompleted', { default: false });
const sortBy = defineModel<ListSortKey>('sortBy', { default: 'recent' });
const search = defineModel<string>('search', { default: '' });

defineEmits<{
  'toggle:status': [ListItemStatus];
  clear: [];
}>();

const STATUS_LABELS: Record<ListItemStatus, string> = {
  not_started: 'NOT_STARTED',
  in_progress: 'IN_PROGRESS',
  completed: 'COMPLETED',
};

const SORT_LABELS: Record<ListSortKey, string> = {
  recent: 'SORT_RECENTLY_WORKED_ON',
  name: 'SORT_NAME_A_Z',
  score_asc: 'SORT_LOWEST_SCORE_FIRST',
  score_desc: 'SORT_HIGHEST_SCORE_FIRST',
};
</script>

<template>
  <div class="row items-center q-gutter-x-sm q-px-sm q-py-xs">
    <span
      v-if="totalCount !== undefined"
      class="text-subtitle2"
    >
      {{ `${shownCount ?? totalCount} / ${totalCount}` }}
    </span>
    <q-space />
    <q-toggle
      v-model="hideCompleted"
      :disable="disable"
      :icon="mdiEyeOffOutline"
      color="secondary"
      keep-color
      :label="$t('HIDE_COMPLETED')"
      class="text-caption"
      dense
    />
    <q-btn
      :disable="disable"
      color="secondary"
      text-color="dark"
      dense
      size="md"
      :icon="mdiFilterVariant"
      :title="$t('FILTERS')"
    >
      <q-badge
        v-if="activeFilterCount > 0"
        floating
        rounded
        color="accent"
        :label="activeFilterCount"
      />
      <q-menu
        anchor="bottom right"
        self="top right"
        class="q-pa-md"
        style="min-width: 260px"
      >
        <div class="text-caption text-weight-medium q-mb-xs">
          {{ $t('STATUS') }}
        </div>
        <div class="row q-gutter-xs q-mb-md">
          <q-chip
            v-for="(label, status) in STATUS_LABELS"
            :key="status"
            clickable
            dense
            :outline="!selectedStatuses.includes(status)"
            color="secondary"
            :text-color="selectedStatuses.includes(status) ? 'dark' : undefined"
            :label="$t(label)"
            @click="$emit('toggle:status', status)"
          />
        </div>
        <div class="text-caption text-weight-medium q-mb-xs">
          {{ $t('SORT_BY') }}
        </div>
        <q-list dense>
          <q-item
            v-for="key in LIST_SORT_KEYS"
            :key="key"
            v-close-popup
            clickable
            :active="sortBy === key"
            active-class="text-secondary text-weight-bold"
            @click="sortBy = key"
          >
            <q-item-section>{{ $t(SORT_LABELS[key]) }}</q-item-section>
          </q-item>
        </q-list>
        <q-input
          v-model="search"
          dense
          clearable
          outlined
          class="q-mt-sm"
          :placeholder="$t('SEARCH_BY_NAME')"
          debounce="300"
        >
          <template #prepend>
            <q-icon
              :name="mdiMagnify"
              size="xs"
            />
          </template>
        </q-input>
        <div class="row justify-end q-mt-sm">
          <q-btn
            v-close-popup
            flat
            dense
            no-caps
            color="accent"
            :label="$t('CLEAR_ALL')"
            @click="$emit('clear')"
          />
        </div>
      </q-menu>
    </q-btn>
  </div>
</template>
